import { getPurchasePeriodsAsArray } from "@taabl/common-lib/calculators/contribution";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { Card, Table } from "react-bootstrap";
import {
    MdDeleteForever,
    MdKeyboardArrowDown,
    MdKeyboardArrowUp,
    MdKeyboardReturn
} from "react-icons/md";
import { toast } from "react-toastify";
import ContributionType from "../components/ContributionType";
import ContributionUpdateModal from "../components/ContributionUpdateModal";
import LoaderButton from "../components/LoaderButton";
import PageLoader from "../components/PageLoader";
import { Constants } from "../libs/Constants";
import { calculateMaxContribution } from "../libs/contributionLib";
import { get, getList, update } from "../libs/dbFunctions";
import { convertDate, convertToDollar, formatCurrency } from "../libs/utils";
import "./Contribution.css";

export default function Contributions(props) {
  const [isLoading, setIsLoading] = useState(false);
  const [contributions, setContributions] = useState([]);
  const [maxContribution, setMaxContribution] = useState(0);
  const [type, setType] = useState(null);
  const [user, setUser] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [show, setShow] = useState(false);

  useEffect(() => {
    onLoad();
  }, [props.isAuthenticated, props.userObj]);
  async function onLoad() {
    setIsLoading(true);
    try {
      let plan = await getPlan();
      //get the user with props.userObj.id
      let userDetails = await get(Constants.dbTypes.USER, props.userObj.id);
      let maximum = calculateMaxContribution(
        plan.maxContribution,
        plan.purchasePeriod,
        userDetails.salary
      );
      setMaxContribution(maximum);

      if (userDetails) {
        setUser(userDetails);
      }
      const objects = await getList(
        Constants.dbTypes.OFFERING_PERIOD_CONTRIBUTIONS,
        props.activeCompany,
        { id: props.userObj.id }
      );
      if (objects && objects.length > 0) {
        setContributions(objects);
      }
    } catch (e) {
      toast.error(e);
    }
    setIsLoading(false);
  }
  async function getPlan() {
    try {
      const planDetail = await getList(
        Constants.dbTypes.PLAN_DETAILS,
        props.activeCompany
      );
      if (planDetail && planDetail.length > 0) {
        return planDetail[0];
      }
    } catch (e) {
      toast.error(e);
    }
  }
  async function handleWithdraw(currentContribution, period) {
    setIsSaving(true);
    let obj = {
      actionType: Constants.CONTRIBUTION_STATUS.WITHDRAW,
      userId: user.id,
      companyId: props.activeCompany.id,
      currentContribution: convertToDollar(currentContribution),
      purchaseExpiration: Number(period),
    };
    await updateRequest(obj);
  }

  async function handleTerminate(currentContribution, period) {
    setIsSaving(true);
    let obj = {
      actionType: Constants.CONTRIBUTION_STATUS.TERMINATE,
      userId: user.id,
      companyId: props.activeCompany.id,
      currentContribution: convertToDollar(currentContribution),
      purchaseExpiration: Number(period),
    };
    await updateRequest(obj);
  }

  function getMessage(ctype) {
    let msg = ctype;
    switch (ctype) {
      case Constants.CONTRIBUTION_STATUS.WITHDRAW:
        msg = "withdrawal";
        break;
      case Constants.CONTRIBUTION_STATUS.TERMINATE:
        msg = "termination";
        break;
    }
    return msg;
  }
  async function updateRequest(obj) {
    try {
      await update(
        Constants.dbTypes.OFFERING_PERIOD_CONTRIBUTIONS,
        obj,
        props.activeCompany.id
      );
      toast.success(
        `Contribution ${getMessage(obj.actionType)} is successful.`
      );
      await onLoad();
      setIsSaving(false);
      return;
    } catch (e) {
      if (e.response && e.response.data && e.response.data.error) {
        toast.error(e.response.data.error);
      } else {
        toast.error(e);
      }
      setIsSaving(false);
    }
  }
  function calculcateCurrentContribution(contributions, period) {
    let date1;
    let date2;
    let currentContribution = 0;
    for (let contribution of contributions) {
      date1 = moment.utc(contribution.periodEnddate);
      date2 = moment.utc(Number(period));
      if (date1.isSame(date2))
        currentContribution =
          currentContribution + contribution.contributionValue;
    }
    return currentContribution;
  }
  function handleHide() {
    setShow(false);
  }
  function handleShow(e) {
    setShow(true);
  }
  function handleIncrease() {
    setType(Constants.CONTRIBUTION_STATUS.INCREASE);
    handleShow();
  }
  function handleDecrease() {
    setType(Constants.CONTRIBUTION_STATUS.DECREASE);
    handleShow();
  }
  function openModal(c, currentContribution) {
    return (
      <ContributionUpdateModal
        show={handleShow}
        onHide={handleHide}
        saveDetails={saveDetails}
        oldcontribution={currentContribution}
        maxcontribution={maxContribution}
        allowtaabl={user.allowTaabl}
        salary={user.salary}
        period={c.purchaseExpiration}
        type={type}
      />
    );
  }
  async function saveDetails(item) {
    setShow(false);
    setIsSaving(true);
    let type;
    if (convertToDollar(item.oldContribution) > item.newContribution)
      type = Constants.CONTRIBUTION_STATUS.DECREASE;
    else type = Constants.CONTRIBUTION_STATUS.INCREASE;
    let obj = {
      actionType: type,
      userId: user.id,
      companyId: props.activeCompany.id,
      updateInContribution: Math.abs(
        Number(item.newContribution) -
        convertToDollar(Number(item.oldContribution))
      ),
      purchaseExpiration: Number(item.period),
    };
    await updateRequest(obj);
  }

  function renderContributionByPeriod(contributions, period) {
    //moved the offerStartdate to periodDt
    let periodDt = convertDate(
      Number(contributions?.offerStartdate),
      Constants.DATEFORMAT.MMDDYYYY,
      false,
      props.activeCompany.timezone
    );
    let isTerminated = false;
    let isWithdrawn = false;
    let isActive = true;
    contributions.purchasePeriods = contributions?.purchasePeriods.sort(
      (a, b) => a.createdAt - b.createdAt
    );
    for (let contribution of contributions?.purchasePeriods) {
      if (contribution.type === Constants.CONTRIBUTION_STATUS.WITHDRAW)
        isWithdrawn = true;
      if (contribution.type === Constants.CONTRIBUTION_STATUS.TERMINATE)
        isTerminated = true;
    }
    let purchaseExpiration = moment.utc(contributions.purchaseExpiration);
    if (moment.utc().isAfter(purchaseExpiration)) {
      isActive = false;
    }
    let currentContribution = calculcateCurrentContribution(
      contributions?.purchasePeriods,
      period
    );
    return (
      <React.Fragment key={period}>
        <tr key={period}>
          <td colSpan={8}>Offering Period - {periodDt}</td>
        </tr>
        {contributions?.purchasePeriods.map((c, index) => {
          return (
            <tr key={`${period}${index}`}>
              <td>
                <ContributionType ctype={c.type}></ContributionType>
              </td>
              <td>
                {convertDate(
                  contributions.createdAt,
                  Constants.DATEFORMAT.MMDDYYYY,
                  false,
                  props.activeCompany.timezone
                )}
              </td>

              <td>
                {convertDate(
                  c.periodStartdate,
                  Constants.DATEFORMAT.MMDDYYYY,
                  false,
                  props.activeCompany.timezone
                )}
              </td>
              <td>
                {convertDate(
                  c.periodEnddate,
                  Constants.DATEFORMAT.MMDDYYYY,
                  false,
                  props.activeCompany.timezone
                )}
              </td>
              <td>
                {convertDate(
                  contributions.purchaseExpiration,
                  Constants.DATEFORMAT.MMDDYYYY,
                  false,
                  props.activeCompany.timezone
                )}
              </td>
              <td>{formatCurrency(convertToDollar(c.contributionValue))}</td>
              <td>
                {formatCurrency(convertToDollar(contributions.purchasePrice))}
              </td>
              {index === 0 && (
                <td
                  rowSpan={contributions?.purchasePeriods?.length}
                  className="action-row"
                >
                  {!isWithdrawn && !isTerminated && (
                    <div>
                      {!user.allowTaabl && (
                        <LoaderButton
                          size="sm"
                          className="action-button"
                          id="increase"
                          value={`${period}`}
                          variant="primary"
                          onClick={handleIncrease}
                          isLoading={isSaving}
                          disabled={!isActive}
                        >
                          <MdKeyboardArrowUp></MdKeyboardArrowUp> Increase
                        </LoaderButton>
                      )}
                      {show && openModal(c, currentContribution)}
                      {!user.allowTaabl && (
                        <LoaderButton
                          size="sm"
                          className="action-button"
                          id="decrease"
                          value={`${period}`}
                          variant="primary"
                          onClick={handleDecrease}
                          isLoading={isSaving}
                          disabled={!isActive}
                        >
                          <MdKeyboardArrowDown></MdKeyboardArrowDown>Decrease
                        </LoaderButton>
                      )}
                      {show && openModal(c, currentContribution)}
                      <LoaderButton
                        size="sm"
                        className="action-button"
                        id="withdraw"
                        value={`${period}`}
                        variant="danger"
                        onClick={() =>
                          handleWithdraw(currentContribution, period)
                        }
                        isLoading={isSaving}
                        disabled={!isActive}
                      >
                        <MdKeyboardReturn></MdKeyboardReturn>Withdraw
                      </LoaderButton>
                      <LoaderButton
                        size="sm"
                        className="action-button"
                        id="terminate"
                        value={`${period}`}
                        variant="danger"
                        onClick={() =>
                          handleTerminate(currentContribution, period)
                        }
                        isLoading={isSaving}
                        disabled={!isActive}
                      >
                        <MdDeleteForever></MdDeleteForever>Terminate
                      </LoaderButton>
                    </div>
                  )}
                </td>
              )}
            </tr>
          );
        })}
      </React.Fragment>
    );
  }

  function renderList() {
    if (contributions && Object.keys(contributions).length > 0) {
      return (
        <PageLoader isActive={isLoading} individual={false}>
          <div className="mt5">
            <Table
              responsive
              bordered
              hover
              size="sm"
              id="output"
              variant="taabl"
            >
              <thead>
                <tr key="headers">
                  <th>Type</th>
                  <th>Created At</th>
                  <th>Period Start Date</th>
                  <th>Period End Date</th>
                  <th>Offering Period End Date</th>
                  <th>Value</th>
                  <th>Purchase Price</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {contributions.map((contribution) => {
                  let peroidsArray = getPurchasePeriodsAsArray(contribution);
                  Object.assign(contribution, {
                    purchasePeriods: peroidsArray,
                  });
                  return renderContributionByPeriod(
                    contribution,
                    contribution.purchaseExpiration
                  );
                })}
              </tbody>
            </Table>
          </div>
        </PageLoader>
        // </div>
      );
    }
    return <div>No contributions for this employee</div>;
  }
  return (
    <div className="container NewNote p5">
      <Card>
        <Card.Title as="h2" className="companies-drop-down mb10">
          Contributions
        </Card.Title>
        <Card.Body>{renderList()}</Card.Body>
      </Card>
    </div>
  );
}
